import React, { Component } from 'react';
import {
  ERROR_MESSAGES, MARGIN_ERROR_FIELDS, MARKET_MARGINS_MODAL_TYPES,
  MAX_ALLOWED_ODDS, MIN_MARGIN_OVERIDE_THRESHOLD, SVG_ICONS,
} from '../../../constants';
import { formatProbability, parseDecimal } from '../../../utils/common';
import validateFields from '../../../utils/validation';
import SVGComponent from '../../../components/SVG/SVGComponent';
import MarginModalLeftSection from './MarginModalLeftSection';
import MarginModalToggleSection from './MarginModalToggleSection';
import MarginModalRightSection from './MarginModalRightSection';

class MarketMarginsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedMarketId: null,
      dropdownOpen: false,
      marketMargins: [{ fromOdds: 1, toOdds: 1000, margin: 1 }],

      marginOverrideEnabled: 0,
      marginOverrideProbabilityThreshold: MIN_MARGIN_OVERIDE_THRESHOLD,
      marginOverrideTimeSlot: 1,

      errorMessage: '',
      errorRow: null,
      errorType: null,
      showError: false,
    };
  }

  componentDidMount() {
    if (this.props.modalType === MARKET_MARGINS_MODAL_TYPES.EDIT) {
      this.populateEditModal();
    }
  }

  componentDidUpdate(_, prevState) {
    if (prevState.marginOverrideEnabled && !this.state.marginOverrideEnabled) {
      this.setState({
        marginOverrideProbabilityThreshold: MIN_MARGIN_OVERIDE_THRESHOLD,
      });
    }
  }

  populateEditModal = () => {
    let marketMarginsEditted;
    if (this.props.marketIdForEdit === 'default') {
      marketMarginsEditted = this.props.defaultMarketMargins;
    } else {
      marketMarginsEditted = this.props.allMarketMargins
        ?.find((marketMargin) => marketMargin?.marketId === this.props.marketIdForEdit);
    }
    this.setState({
      selectedMarketId: marketMarginsEditted?.marketId,
      dropdownOpen: false,
      marketMargins: marketMarginsEditted?.levels,

      marginOverrideEnabled: marketMarginsEditted?.marginOverride?.enabled || 0,
      marginOverrideTimeSlot: marketMarginsEditted?.marginOverride?.fromSlot,
      marginOverrideProbabilityThreshold:
        (formatProbability(marketMarginsEditted?.marginOverride?.probabilityThreshold)
          || 0)?.toFixed(0),
    });
  };

  toggleDropdown = () => {
    this.setState({
      dropdownOpen: !this.state.dropdownOpen,
    });
  };

  selectMarket = (marketId) => {
    this.setState({
      selectedMarketId: marketId,
      dropdownOpen: false,
    });
  };

  getSelectedMarketName = () => this.props.markets?.find((market) => market?.id
    === this.state.selectedMarketId)?.name;

  handleFromChange = (e, idx) => {
    e.persist();
    const marketMargins = [...this.state.marketMargins];
    const fromValue = { ...marketMargins?.find((_, i) => i === idx) };

    fromValue.fromOdds = Number(e.target.value);
    marketMargins[idx] = fromValue;
    this.setState({
      marketMargins,
    });
  };

  handleMarginChange = (e, idx) => {
    e.persist();
    const marketMargins = [...this.state.marketMargins];
    const marketMargin = { ...marketMargins?.find((_, i) => i === idx) };

    marketMargin.margin = Number(e.target.value);
    marketMargins[idx] = marketMargin;
    this.setState({
      marketMargins,
    });
  };

  addInterval = () => {
    const marketMargins = [...this.state.marketMargins];
    marketMargins.push({ fromOdds: 0, margin: 1 });
    this.setState({
      marketMargins,
    });
  };

  removeMargin = () => {
    const marketMargins = [...this.state.marketMargins];
    marketMargins.pop();
    this.setState({
      marketMargins,
    });
  };

  disableAddIntervalButton = () => {
    const lastMarketMargin = {
      ...this.state.marketMargins?.[this.state.marketMargins?.length - 1],
    };
    if (lastMarketMargin.fromOdds === 0) {
      return true;
    }
    return false;
  };

  getToOddValue = (idx) => {
    if (idx === this.state.marketMargins?.length - 1) {
      return 1000;
    }
    if (this.state.marketMargins?.[idx + 1]?.fromOdds === 0) {
      return 0;
    }
    return (this.state.marketMargins?.[idx + 1]?.fromOdds - 0.01).toFixed(2);
  };

  toggleMarketOverride = () => {
    this.setState({
      marginOverrideEnabled: this.state.marginOverrideEnabled ? 0 : 1,
    });
  };

  handleOverideProbabilityThresholdChange = (e) => {
    e.persist();
    const value = Number(e.target.value)?.toFixed(0);
    this.setState({
      marginOverrideProbabilityThreshold: value,
    });
  };

  handleMarginOverrideTimeSlot = (e) => {
    this.setState({
      marginOverrideTimeSlot: Number(e.target.value),
    });
  };

  addMarketMargin = () => {
    const newMarketMargin = {
      marketId: this.state.selectedMarketId,
      levels: this.state.marketMargins,

      hasChanges: true,
    };
    if (!this.props.isTennis) {
      newMarketMargin.marginOverride = {
        enabled: this.state.marginOverrideEnabled,
        fromSlot: this.state.marginOverrideTimeSlot,
      };
      if (this.state.marginOverrideProbabilityThreshold) {
        newMarketMargin.marginOverride
          .probabilityThreshold = parseDecimal(this.state.marginOverrideProbabilityThreshold, 100);
      }
    }

    this.props.addNewMarketMargin(newMarketMargin);
  };

  validateMarginsSection = () => {
    for (let i = 0; i < this.state.marketMargins.length; i += 1) {
      // check odds
      if (i > 0
        && this.state.marketMargins[i].fromOdds <= this.state.marketMargins[i - 1].fromOdds) {
        this.setState({
          errorType: MARGIN_ERROR_FIELDS.FROM_ODDS,
          errorRow: i + 1,
        });
        return ERROR_MESSAGES.MARKET_MARGINS.CANNOT_OVERLAP;
      }
      if (this.state.marketMargins[i].fromOdds >= MAX_ALLOWED_ODDS) {
        this.setState({
          errorType: MARGIN_ERROR_FIELDS.FROM_ODDS,
          errorRow: i + 1,
        });
        return ERROR_MESSAGES.MARKET_MARGINS.LESS_THAN_1000;
      }

      // check margins
      if (this.state.marketMargins[i].margin < 1.01 || this.state.marketMargins[i].margin > 2) {
        this.setState({
          errorType: MARGIN_ERROR_FIELDS.MARGINS,
          errorRow: i + 1,
        });
        return ERROR_MESSAGES.MARKET_MARGINS.MARGIN_RANGE;
      }
    }

    this.setState({
      errorType: null,
      errorRow: null,
    });
    return '';
  };

  validateMarketMarginAndAdd = () => {
    if (this.state.showError) {
      this.setState({
        showError: false,
      });
    }

    const errorMessage = this.validateMarginsSection();
    if (errorMessage) {
      this.setState({
        errorMessage,
        showError: true,
      });
      return;
    }

    const validationParams = [
      {
        label: 'Margin Override Probability Threshold',
        fieldName: 'marginOverrideProbabilityThreshold',
        value: this.state.marginOverrideProbabilityThreshold,
        rules: ['betweenXAndY'],
        minValue: 80,
        comparatorMaxValue: 100,
        getValue: (val) => val,
      },
      {
        label: 'Margin Override Time Slot',
        fieldName: 'marginOverrideTimeSlot',
        value: this.state.marginOverrideTimeSlot,
        rules: ['betweenXAndY'],
        minValue: 1,
        comparatorMaxValue: 90,
        getValue: (val) => val,
      },
    ];

    if (this.state.marginOverrideEnabled) {
      validateFields(validationParams)
        .then(() => {
          this.addMarketMargin();
        }).catch((errorMgs) => {
          let error = '';
          if (errorMgs?.marginOverrideTimeSlot) {
            error = errorMgs.marginOverrideTimeSlot.props.children;
          }

          if (errorMgs?.marginOverrideProbabilityThreshold) {
            error = errorMgs?.marginOverrideProbabilityThreshold.props.children;
          }
          this.setState({
            errorMessage: error,
            showError: true,
          });
        });
    } else {
      this.addMarketMargin();
    }
  };

  disableConfirmButton = () => {
    const lastMarketMargin = this.state.marketMargins?.[this.state.marketMargins?.length - 1];
    return this.state.selectedMarketId === null
      || Number(lastMarketMargin?.fromOdds) === 0
      || Number(lastMarketMargin?.margin) === 0
      || (this.state.marginOverrideEnabled
        && !this.state.marginOverrideProbabilityThreshold);
  };

  getMarketName = () => {
    if (this.props.modalType === MARKET_MARGINS_MODAL_TYPES.EDIT
      && this.state.selectedMarketId === this.props.defaultMarketMargins?.marketId) {
      return 'Default';
    }
    if (this.state.selectedMarketId) {
      return this.getSelectedMarketName();
    }
    return 'Select Market';
  };

  render() {
    const availableMarkets = this.props.getAvailableMarkets();

    return (
      <div className="modal modal--open">
        <div className={`dialog ${!this.props.isTennis && 'dialog--selection'}`}>
          <div className="dialog__heading">
            <span className="dialog__title">
              {this.props.modalType}
              {' '}
              Market Margins
            </span>
            <SVGComponent
              className="dialog__close"
              src={`${SVG_ICONS.utility}#close`}
              onClick={this.props.closeMarketMarginsModal}
            />
          </div>

          <div className="dialog__content">

            {this.state.showError && (
              <div className="dialog__error m-b-45">
                <div className="dialog__error-txt">
                  {this.state.errorRow ? `Row-${this.state.errorRow}: ` : ''}
                  {this.state.errorMessage}
                </div>
              </div>
            )}

            <div className="d-flex">

              <div className="w-100 dialog__margins-value">

                <MarginModalToggleSection
                  toggleDropdown={this.toggleDropdown}
                  modalType={this.props.modalType}
                  dropdownOpen={this.state.dropdownOpen}
                  getMarketName={this.getMarketName}
                  availableMarkets={availableMarkets}
                  selectMarket={this.selectMarket}
                />

                <MarginModalLeftSection
                  marketMargins={this.state.marketMargins}
                  handleFromChange={this.handleFromChange}
                  getToOddValue={this.getToOddValue}
                  handleMarginChange={this.handleMarginChange}
                  removeMargin={this.removeMargin}
                  errorType={this.state.errorType}
                  errorRow={this.state.errorRow}
                />

              </div>
              {!this.props.isTennis && (
                <div className="w-100 p-l-30">
                  <MarginModalRightSection
                    marginOverrideEnabled={this.state.marginOverrideEnabled}
                    toggleMarketOverride={this.toggleMarketOverride}
                    marginOverrideProbabilityThreshold={this.state.marginOverrideProbabilityThreshold}
                    handleOverideProbabilityThresholdChange={
                      this.handleOverideProbabilityThresholdChange
                    }
                    marginOverrideTimeSlot={this.state.marginOverrideTimeSlot}
                    handleMarginOverrideTimeSlot={this.handleMarginOverrideTimeSlot}
                  />
                </div>

              )}
            </div>
          </div>
          <div className="dialog__btn-wrap">
            <button
              type="button"
              className="btn btn-quaternary mr-auto"
              onClick={this.addInterval}
              disabled={this.disableAddIntervalButton()}
            >
              + Add Interval
            </button>
            {' '}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={this.props.closeMarketMarginsModal}
            >
              Cancel
            </button>
            {' '}
            <button
              type="button"
              className="btn btn-primary m-l-10"
              disabled={this.disableConfirmButton()}
              onClick={this.validateMarketMarginAndAdd}
            >
              Confirm
            </button>
          </div>
        </div>

      </div>
    );
  }
}

export default MarketMarginsModal;
